/**
 * Masters → Entity → Audit. Client sheet rows 18-20, 22, 23-24.
 * @file src/web/features/admin/components/masters/AuditTab.tsx
 *
 * The CA sense of "audit" — statutory and tax audit applicability, who the
 * auditors are, and who is authorised to sign. Not the activity log, which
 * lives at /admin/audit and answers a completely different question.
 *
 * Applicability is tri-state (applicable / not applicable / not yet decided)
 * for the same reason the statutory registrations are: an unanswered question
 * and a deliberate "no" look identical once the field is blank, and only the
 * person filling it in knows which one it is.
 *
 * DSC: metadata only. There is no field here for a certificate, password or
 * PIN, and there should never be one — see the AuthorizedSignatory model.
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, Trash2, ShieldCheck, AlertTriangle, UserCheck } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetchJson } from '../../lib/admin-api';
import {
  Card, Field, TextInput, Select, PrimaryBtn, GhostBtn, AddToggle, FormError,
  Loading, ErrBox, Pill, mutate, useDeleteConfirm,
} from './shared';

interface Auditor {
  id: string; role: string; firmName: string; frn: string | null;
  partnerName: string | null; membershipNo: string | null;
  appointedFrom: string | null; appointedTo: string | null; status: string;
}
interface Signatory {
  id: string; name: string; designation: string | null; pan: string | null; din: string | null;
  authorizedFor: string[];
  dscSerial: string | null; dscIssuer: string | null;
  dscValidFrom: string | null; dscValidTo: string | null; dscHolderName: string | null;
  status: string;
}
interface AuditProfile {
  taxAuditApplicable: boolean | null;
  statutoryAuditApplicable: boolean | null;
  accountingStandard: string | null;
  auditors: Auditor[];
  signatories: Signatory[];
}

const ROLE_LABEL: Record<string, string> = {
  statutory: 'Statutory auditor', tax: 'Tax auditor',
  internal: 'Internal auditor', cost: 'Cost auditor',
};
const SCOPES = ['GST', 'INCOME_TAX', 'ROC', 'TDS', 'PF', 'ESI'];
const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null);

/** Days until a DSC lapses. Negative means it already has. */
const daysLeft = (v: string | null) =>
  v === null ? null : Math.ceil((new Date(v).getTime() - Date.now()) / 86_400_000);

export function AuditTab({ entityId }: { entityId: string | null }) {
  const [d, setD] = useState<AuditProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingAuditor, setAddingAuditor] = useState(false);
  const [addingSignatory, setAddingSignatory] = useState(false);
  const { askDelete, deleteDialog } = useDeleteConfirm();

  const load = useCallback(() => {
    if (!entityId) { setError('Select an entity in the header.'); return; }
    adminFetchJson<AuditProfile>('/api/masters/audit-profile', { query: { entityId } })
      .then((x) => { setD(x); setError(null); })
      .catch((e: Error) => setError(e.message));
  }, [entityId]);
  useEffect(load, [load]);

  const setFlag = async (patch: Record<string, unknown>) => {
    try { await mutate('audit-profile', 'PATCH', { entityId, ...patch }); load(); }
    catch (e) { toast.error((e as Error).message); }
  };

  const remove = async (path: string) => {
    try { await mutate(path, 'DELETE', { entityId }); toast.success('Removed'); load(); }
    catch (e) { toast.error((e as Error).message); }
  };

  const expiring = (d?.signatories ?? []).filter((s) => {
    const n = daysLeft(s.dscValidTo);
    return n !== null && n < 30;
  });
  const undecided = d
    ? [d.taxAuditApplicable, d.statutoryAuditApplicable].filter((v) => v === null || v === undefined).length
    : 0;
  const summary = !d && !error ? 'loading…'
    : error ? 'could not load'
    : expiring.length ? `· ${expiring.length} DSC expiring or expired`
    : undecided ? `· ${undecided} undecided`
    : `· ${d!.auditors.length} auditors, ${d!.signatories.length} signatories`;

  return (
    <Card
      collapsible
      defaultOpen={false}
      summary={summary}
      title="Audit"
      subtitle="Whether this client is audited, by whom, and who is authorised to sign their filings. Applicability drives the gap report — an unanswered question is itself a gap."
    >
      {!d && !error && <Loading label="Loading audit profile" />}
      {error && <div className="px-6 py-5"><ErrBox msg={error} /></div>}

      {d && !error && (
        <div className="divide-y divide-ink-100">
          {/* ── applicability (rows 18-20) ─────────────────────────────── */}
          <div className="px-6 py-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-400">Applicability</p>
            <div className="space-y-3">
              <TriState
                label="Tax audit (Sec 44AB)"
                help="Turnover-based. Decides whether a tax auditor and a 3CA/3CB-3CD are expected."
                value={d.taxAuditApplicable}
                onChange={(v) => setFlag({ taxAuditApplicable: v })}
              />
              <TriState
                label="Statutory audit"
                help="Companies and larger LLPs are audited regardless of turnover."
                value={d.statutoryAuditApplicable}
                onChange={(v) => setFlag({ statutoryAuditApplicable: v })}
              />
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ink-200 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-800">AS / Ind AS</p>
                  <p className="mt-0.5 text-xs text-ink-500">
                    Ind AS applies above the net-worth threshold; the answer changes the format of the financials.
                  </p>
                </div>
                <div className="w-44 shrink-0">
                  <Select
                    value={d.accountingStandard ?? ''}
                    onChange={(v) => setFlag({ accountingStandard: v || null })}
                    options={[
                      { value: 'AS', label: 'AS' },
                      { value: 'IndAS', label: 'Ind AS' },
                      { value: 'none', label: 'Neither' },
                    ]}
                    placeholder="Not recorded"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── auditors (rows 23-24) ──────────────────────────────────── */}
          <div className="px-6 py-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Auditors</p>
              <AddToggle open={addingAuditor} onToggle={() => setAddingAuditor(!addingAuditor)} label="Add auditor" />
            </div>

            {addingAuditor && (
              <AuditorForm
                entityId={entityId}
                onDone={() => { setAddingAuditor(false); load(); }}
                onCancel={() => setAddingAuditor(false)}
              />
            )}

            {d.auditors.length === 0 && !addingAuditor ? (
              <p className="text-sm text-ink-500">
                None recorded. A tax auditor is expected once Sec 44AB is marked applicable, and a statutory
                auditor once statutory audit is.
              </p>
            ) : (
              <ul className="space-y-2">
                {d.auditors.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-ink-200 px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Pill tone="brand">{ROLE_LABEL[a.role] ?? a.role}</Pill>
                        <span className="text-sm font-semibold text-ink-800">{a.firmName}</span>
                        {a.status !== 'active' && <Pill tone="neutral">{a.status}</Pill>}
                      </div>
                      <p className="mt-1 text-xs text-ink-500">
                        {[
                          a.frn && `FRN ${a.frn}`,
                          a.partnerName && `Partner: ${a.partnerName}`,
                          a.membershipNo && `M. No. ${a.membershipNo}`,
                          fmtDate(a.appointedFrom) && `From ${fmtDate(a.appointedFrom)}`,
                          fmtDate(a.appointedTo) && `To ${fmtDate(a.appointedTo)}`,
                        ].filter(Boolean).join(' · ') || 'No further details recorded'}
                      </p>
                    </div>
                    <GhostBtn tone="danger" onClick={() => askDelete(`${ROLE_LABEL[a.role] ?? a.role} ${a.firmName}`, () => remove(`auditors/${a.id}`))}>
                      <Trash2 className="h-3 w-3" />
                    </GhostBtn>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── signatories + DSC (row 22) ─────────────────────────────── */}
          <div className="px-6 py-5">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Authorized signatories</p>
              <AddToggle open={addingSignatory} onToggle={() => setAddingSignatory(!addingSignatory)} label="Add signatory" />
            </div>
            <p className="mb-3 max-w-3xl text-xs text-ink-500">
              DSC details are recorded so a filing is never attempted with a lapsed certificate. Only the
              holder, issuer, serial and validity are stored — never the certificate, its password or its PIN.
            </p>

            {addingSignatory && (
              <SignatoryForm
                entityId={entityId}
                onDone={() => { setAddingSignatory(false); load(); }}
                onCancel={() => setAddingSignatory(false)}
              />
            )}

            {d.signatories.length === 0 && !addingSignatory ? (
              <p className="text-sm text-ink-500">
                None recorded. Every return is signed by someone — record who, and when their DSC expires.
              </p>
            ) : (
              <ul className="space-y-2">
                {d.signatories.map((s) => {
                  const n = daysLeft(s.dscValidTo);
                  return (
                    <li key={s.id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-ink-200 px-4 py-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <UserCheck className="h-3.5 w-3.5 shrink-0 text-ink-400" aria-hidden />
                          <span className="text-sm font-semibold text-ink-800">{s.name}</span>
                          {s.designation && <span className="text-xs text-ink-500">{s.designation}</span>}
                          {n === null
                            ? <Pill tone="warn">DSC validity not recorded</Pill>
                            : n < 0
                              ? <Pill tone="danger"><AlertTriangle className="h-3 w-3" />DSC expired {fmtDate(s.dscValidTo)}</Pill>
                              : n < 30
                                ? <Pill tone="warn"><AlertTriangle className="h-3 w-3" />DSC expires in {n} days</Pill>
                                : <Pill tone="ok"><Check className="h-3 w-3" />DSC valid to {fmtDate(s.dscValidTo)}</Pill>}
                        </div>
                        <p className="mt-1 text-xs text-ink-500">
                          {[
                            s.pan && `PAN ${s.pan}`,
                            s.din && `DIN ${s.din}`,
                            s.dscIssuer && `Issuer: ${s.dscIssuer}`,
                            s.dscSerial && `Serial ${s.dscSerial}`,
                          ].filter(Boolean).join(' · ') || 'No further details recorded'}
                        </p>
                        {s.authorizedFor.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {s.authorizedFor.map((sc) => (
                              <Pill key={sc} tone="neutral"><ShieldCheck className="h-3 w-3" />{sc.replace('_', ' ')}</Pill>
                            ))}
                          </div>
                        )}
                      </div>
                      <GhostBtn tone="danger" onClick={() => askDelete(`signatory ${s.name}`, () => remove(`signatories/${s.id}`))}>
                        <Trash2 className="h-3 w-3" />
                      </GhostBtn>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
      {deleteDialog}
    </Card>
  );
}

/* ── tri-state applicability control ────────────────────────────────────── */

/**
 * Three buttons rather than a checkbox, because a checkbox has two states and
 * this has three. "Not decided" has to be visibly distinct from "no", or the
 * gap report and the screen disagree about what is outstanding.
 */
function TriState({ label, help, value, onChange }: {
  label: string; help: string; value: boolean | null; onChange: (v: boolean | null) => void;
}) {
  const opts: { v: boolean | null; l: string }[] = [
    { v: true, l: 'Applicable' },
    { v: false, l: 'Not applicable' },
    { v: null, l: 'Not decided' },
  ];
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ink-200 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink-800">{label}</p>
        <p className="mt-0.5 text-xs text-ink-500">{help}</p>
      </div>
      <div className="flex shrink-0 gap-1.5">
        {opts.map(({ v, l }) => {
          const on = value === v || (v === null && (value === null || value === undefined));
          return (
            <button
              key={l}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(v)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                on
                  ? v === null
                    ? 'border-warn-300 bg-warn-50 text-warn-700'
                    : 'border-highlighter bg-highlighter-40 text-inkblack'
                  : 'border-ink-200 bg-paper text-ink-500 hover:bg-ink-50'
              }`}
            >
              {l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── forms ──────────────────────────────────────────────────────────────── */

function AuditorForm({ entityId, onDone, onCancel }: {
  entityId: string | null; onDone: () => void; onCancel: () => void;
}) {
  const [f, setF] = useState({
    role: 'statutory', firmName: '', frn: '', partnerName: '', membershipNo: '',
    appointedFrom: '', appointedTo: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate('auditors', 'POST', { entityId, ...f });
      toast.success('Auditor added'); onDone();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-ink-200 bg-ink-50/60 p-4">
      <FormError msg={err} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Role">
          <Select value={f.role} onChange={set('role')} options={Object.entries(ROLE_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
        </Field>
        <Field label="Firm name"><TextInput value={f.firmName} onChange={set('firmName')} placeholder="R. Rao & Co" /></Field>
        <Field label="FRN" hint="ICAI firm registration number."><TextInput value={f.frn} onChange={(v) => set('frn')(v.toUpperCase())} mono /></Field>
        <Field label="Signing partner"><TextInput value={f.partnerName} onChange={set('partnerName')} /></Field>
        <Field label="Membership no"><TextInput value={f.membershipNo} onChange={set('membershipNo')} mono /></Field>
        <div />
        <Field label="Appointed from"><TextInput type="date" value={f.appointedFrom} onChange={set('appointedFrom')} /></Field>
        <Field label="Appointed to" hint="Leave blank if the appointment is current."><TextInput type="date" value={f.appointedTo} onChange={set('appointedTo')} /></Field>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <PrimaryBtn onClick={submit} busy={busy}>Add auditor</PrimaryBtn>
        <button onClick={onCancel} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
      </div>
    </div>
  );
}

function SignatoryForm({ entityId, onDone, onCancel }: {
  entityId: string | null; onDone: () => void; onCancel: () => void;
}) {
  const [f, setF] = useState({
    name: '', designation: '', pan: '', din: '',
    dscSerial: '', dscIssuer: '', dscValidFrom: '', dscValidTo: '', dscHolderName: '',
  });
  const [scopes, setScopes] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate('signatories', 'POST', { entityId, ...f, authorizedFor: scopes });
      toast.success('Signatory added'); onDone();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-ink-200 bg-ink-50/60 p-4">
      <FormError msg={err} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Name"><TextInput value={f.name} onChange={set('name')} /></Field>
        <Field label="Designation">
          <Select value={f.designation} onChange={set('designation')}
            options={['Director', 'Partner', 'Proprietor', 'Authorised Representative', 'Company Secretary']} />
        </Field>
        <Field label="PAN"><TextInput value={f.pan} onChange={(v) => set('pan')(v.toUpperCase())} mono /></Field>
        <Field label="DIN" hint="Companies only."><TextInput value={f.din} onChange={set('din')} mono /></Field>
      </div>

      <p className="mb-2 mt-4 text-xs font-medium text-ink-600">Authorised to sign</p>
      <div className="flex flex-wrap gap-2">
        {SCOPES.map((sc) => {
          const on = scopes.includes(sc);
          return (
            <button
              key={sc}
              type="button"
              aria-pressed={on}
              onClick={() => setScopes((p) => (on ? p.filter((x) => x !== sc) : [...p, sc]))}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                on ? 'border-highlighter bg-highlighter-40 text-inkblack' : 'border-ink-200 bg-paper text-ink-500 hover:bg-ink-50'
              }`}
            >
              {on && <Check className="h-3 w-3" aria-hidden />}
              {sc.replace('_', ' ')}
            </button>
          );
        })}
      </div>

      <p className="mb-2 mt-4 text-xs font-medium text-ink-600">
        Digital signature certificate <span className="font-normal text-ink-400">— details only, never the certificate itself</span>
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Issuer"><TextInput value={f.dscIssuer} onChange={set('dscIssuer')} placeholder="eMudhra" /></Field>
        <Field label="Serial"><TextInput value={f.dscSerial} onChange={set('dscSerial')} mono /></Field>
        <Field label="Valid from"><TextInput type="date" value={f.dscValidFrom} onChange={set('dscValidFrom')} /></Field>
        <Field label="Valid to" hint="Drives the expiry warning."><TextInput type="date" value={f.dscValidTo} onChange={set('dscValidTo')} /></Field>
        <div className="sm:col-span-2">
          <Field label="Name on certificate" hint="Only if it differs from the name above.">
            <TextInput value={f.dscHolderName} onChange={set('dscHolderName')} />
          </Field>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <PrimaryBtn onClick={submit} busy={busy}>Add signatory</PrimaryBtn>
        <button onClick={onCancel} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
      </div>
    </div>
  );
}
